import axios from "axios";

export const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, "");

const api = axios.create({ baseURL: API_BASE_URL });

// Report tables and selectors must read every page, not only the API default.
export const getAllRecords = async (url, params = {}) => {
  const limit = 100;
  const first = await api.get(url, { params: { ...params, page: 1, limit } });
  const data = first.data?.data || [];
  const count = Number(first.data?.count);
  if (!Number.isFinite(count) || data.length >= count) return data;
  const requests = [];
  for (let page = 2; (page - 1) * limit < count; page += 1) requests.push(api.get(url, { params: { ...params, page, limit } }));
  const remaining = await Promise.all(requests);
  return data.concat(...remaining.map((response) => response.data?.data || []));
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("uride_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Compatibility for the already-running API until it is restarted with the
  // updated Company User schema. These fields remain hidden from the form.
  if (config.method === "post" && config.url === "/company-users" && config.data) {
    config.data.username = config.data.username || `user-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    config.data.fullName = config.data.fullName || config.data.username;
    config.data.employeeId = config.data.employeeId || `USR-${Date.now()}`;
  }
  return config;
});

export const openProtectedFile = async (filePath) => {
  // Keep the request under the API base URL. On the live site the reverse
  // proxy exposes the backend at /api, while local development uses the same
  // route through Express.
  const response = await api.get(filePath, { responseType: "blob" });
  const contentType = String(response.headers["content-type"] || "").toLowerCase();
  if (!contentType.startsWith("image/") && contentType !== "application/pdf") {
    // A reverse proxy can return the React page with a successful (200)
    // response when an upload is missing. Do not open that page as a blank
    // Blob tab and make the real problem visible to the user instead.
    throw new Error("The proof file is unavailable or the server returned an invalid file.");
  }
  const url = URL.createObjectURL(response.data);
  window.open(url, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// Existing document links keep their current appearance, but are fetched with
// the JWT instead of exposing uploads publicly.
if (typeof window !== "undefined") {
  document.addEventListener("click", (event) => {
    const link = event.target.closest("a[href]");
    if (!link) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== API_ORIGIN || !url.pathname.startsWith("/uploads/")) return;
    event.preventDefault();
    openProtectedFile(url.pathname).catch(() => window.alert("Unable to open this file."));
  });
}

export default api;
